/* eslint-disable import/prefer-default-export */
import { DATE_INPUT_TEXT_FORMAT } from "src/constants/format";
import dayjs, { Dayjs, isDayjs } from "dayjs";
import { FormInstance } from "sunday-component/node_modules/antd";
import { RuleObject } from "antd/lib/form";
import { InitialValuesType } from "../types/entryForm";
import {
  ActiveInputCodesType,
  FieldValidationConfigType,
} from "../types/productCategory";
import { RULE_CODE } from "../constants/input";
import {
  DECIMAL_PATTERN,
  E164_CONTACT_NO_PATTERN,
  EMAIL_PATTERN,
} from "./regEx";
import { calcAgeByDay0, calcAgeByYear0 } from "./age";
import { formatDateText } from "./date";

export type ValidateRuleCodeOutput = {
  code: string;
  isError: boolean;
};

export type ValidateInputCodesOutput = {
  code: string;
  isError: boolean;
  rulesValidation: ValidateRuleCodeOutput[];
};

/**
 * verify that the variable has a value except false.
 * @param value value for validation.
 * @returns required validation result.
 */
export const validateRequiredExcludeFalseRule = (value: any): boolean => {
  if (!value && value !== 0) return false;
  return true;
};

/**
 * verify that the variable has a value.
 * @param value value for validation.
 * @returns required validation result.
 */
export const validateRequiredRule = (value: any): boolean => {
  if (!value && value !== 0 && value !== false) {
    return false;
  }
  // Dayjs date case
  if (value && isDayjs(value) && !value?.isValid()) {
    return false;
  }

  return true;
};

/**
 * verify that the variable has in date range from now.
 * @param from number of date before current date.
 * @param to number of date after current date.
 * @param value selected date for validation.
 * @returns in range date from now validation result.
 */
export const validateRangeFromNowRule = (
  value: Date | Dayjs,
  from: number | null = null,
  to: number | null = null
): boolean => {
  const startDay = dayjs()
    .add(from - 1, "days")
    .endOf("day");
  const lastDay = dayjs().add(to, "days").endOf("day");
  const valueDayjs = dayjs(value, DATE_INPUT_TEXT_FORMAT);

  if (
    ((from === 0 || from) && valueDayjs < startDay) ||
    ((to === 0 || to) && valueDayjs > lastDay)
  ) {
    return false;
  }

  return true;
};

export const handleDisabledDate = (
  validations: FieldValidationConfigType[]
) => {
  return (current: Date) => {
    const rangeFromNowRule = validations.find(
      (rule) => rule.rule === RULE_CODE.RANGE_FROM_NOW
    );

    if (rangeFromNowRule) {
      const { from, to } = rangeFromNowRule.params;
      return !validateRangeFromNowRule(current, from, to);
    }

    return false;
  };
};

/**
 * Validates the range calculated from a date to ensure it falls within a given range of years.
 * @param {Dayjs} date - The date as a Dayjs object.
 * @param {number} startYear - The start year of the range to validate against.
 * @param {number} endYear - The end year of the range to validate against.
 * @returns {boolean} - Returns true if the date is valid within the specified range, false otherwise.
 */
export const validateFullYearRangeFromTo = (
  date: Dayjs,
  startYear: number,
  endYear: number
): boolean => {
  const currentDate = dayjs();
  const diff = currentDate.diff(date, "years");

  return diff >= startYear && diff <= endYear;
};

/**
 * Validates the range calculated from a date to ensure it falls within a given range of years.
 * @param {Dayjs} date - The date as a Dayjs object.
 * @param {number} startYear - The start year of the range to validate against.
 * @param {number} endYear - The end year of the range to validate against.
 * @returns {boolean} - Returns true if the date is valid within the specified range, false otherwise.
 */
export const validateAgeBetween = (
  date: Dayjs,
  startYear: number,
  endYear: number
): boolean => {
  const currentDate = dayjs();
  const diff = currentDate.year() - date.year();
  return diff >= startYear && diff <= endYear;
};

/**
 * verify that the variable has in list.
 * @param list list of values.
 * @param value value for in list rule validation.
 * @returns in list validator validation result.
 */
export const validateInListRule = (list: Array<any>, value: any): boolean => {
  if (!list.includes(value)) {
    return false;
  }
  return true;
};

/**
 * verify that the variable is correct followed the rule.
 * @param activeInputCodes list of input codes.
 * @param values value for validation.
 * @returns in list of validation result.
 */
export const validateValueByActiveInputCode = (
  activeInputCodes: ActiveInputCodesType[],
  values: Partial<InitialValuesType>
): ValidateInputCodesOutput[] => {
  return activeInputCodes.map((activeInputCode) => {
    const rulesValidation = activeInputCode.validations.map((rule) => {
      let isRuleValidationError = false;

      switch (rule.rule) {
        case RULE_CODE.REQUIRED:
          isRuleValidationError = !validateRequiredRule(
            values?.[activeInputCode.inputCode]
          );
          break;
        case RULE_CODE.RANGE_FROM_NOW:
          isRuleValidationError = !validateRangeFromNowRule(
            values?.[activeInputCode.inputCode],
            rule.params.from,
            rule.params.to
          );
          break;
        case RULE_CODE.IN:
          isRuleValidationError = !validateInListRule(
            rule.params.values,
            values?.[activeInputCode.inputCode]
          );
          break;
        case RULE_CODE.LICENSE_PLATE:
          isRuleValidationError = !validateLicensePlate(
            values?.[activeInputCode.inputCode]
          );
          break;
      }

      return {
        code: rule.rule,
        isError: isRuleValidationError,
      };
    });

    const isInputValidationError =
      rulesValidation.filter((rule) => rule.isError).length > 0;

    return {
      code: activeInputCode.inputCode,
      isError: isInputValidationError,
      rulesValidation,
    };
  });
};

const genErrorMessage = (
  message,
  ErrorMessageComponent
): string | JSX.Element => {
  if (ErrorMessageComponent) {
    return <ErrorMessageComponent>{message}</ErrorMessageComponent>;
  }

  return message;
};

/**
 * transform appForm validations to ant validations.
 * @param validations appForm validations.
 * @param t i18 translator.
 * @param label field label.
 * @returns ant validation.
 */
export const transformToAntValidations = (
  validations: FieldValidationConfigType[],
  t: Function,
  label: string,
  isPreferWhitespace: boolean = false,
  ErrorMessageComponent: React.ElementType = null,
  isChoice: boolean = false
): RuleObject[] => [
  {
    required: !!validations?.find(
      (validation) => validation.rule === RULE_CODE.REQUIRED
    ),
    message: genErrorMessage(
      t(
        isChoice
          ? "form:validation.selectedRequired"
          : "form:validation.required",
        { field: label }
      ),
      ErrorMessageComponent
    ),
    ...(isPreferWhitespace
      ? {
          whitespace: !!validations?.find(
            (validation) => validation.rule === RULE_CODE.REQUIRED
          ),
        }
      : null),
  },
  {
    validator: async (_: any, value: any = "") => {
      const characterLengthValidation = validations?.find(
        (validation) => validation.rule === RULE_CODE.CHARACTER_LENGTH
      );
      const idCardValidation = validations?.find(
        (validation) => validation.rule === RULE_CODE.TH_ID_CARD
      );
      const isFullName = validations?.find(
        (validation) => validation.rule === RULE_CODE.FULL_NAME
      );
      const emailValidation = validations?.find(
        (validation) => validation.rule === RULE_CODE.EMAIL
      );
      const phoneNumberValidation = validations?.find(
        (validation) => validation.rule === RULE_CODE.PHONE_NUMBER
      );
      if (characterLengthValidation) {
        const max = characterLengthValidation?.params?.max;
        const min = characterLengthValidation?.params?.min;
        if (
          (max &&
            value?.trim?.().length > characterLengthValidation?.params?.max) ||
          (min &&
            value?.trim?.().length < characterLengthValidation?.params?.min)
        ) {
          // validate only min length
          if (min && !max) {
            return Promise.reject(
              genErrorMessage(
                t("form:validation.characterLengthMin", {
                  min,
                }),
                ErrorMessageComponent
              )
            );
          }

          // validate only max length
          if (!min && max) {
            return Promise.reject(
              genErrorMessage(
                t("form:validation.characterLengthMax", {
                  max,
                }),
                ErrorMessageComponent
              )
            );
          }

          if (min === max) {
            return Promise.reject(
              genErrorMessage(
                t("form:validation.characterLengthEqualMinMax", {
                  min,
                }),
                ErrorMessageComponent
              )
            );
          }

          // validate min and max length
          return Promise.reject(
            genErrorMessage(
              t("form:validation.characterLengthMinMax", {
                min,
                max,
              }),
              ErrorMessageComponent
            )
          );
        }
      }

      // blame him if it wrong
      // https://gist.github.com/peatiscoding/a16840caed0ba1b29e6b2fe171565ea0#file-assertthaiid-ts
      if (idCardValidation) {
        const idCard = value?.trim()?.match(/(\d{12})(\d)/);
        if (!idCard) {
          return Promise.reject(
            genErrorMessage(
              t("form:validation.idCardValidation"),
              ErrorMessageComponent
            )
          );
        }
        const digits = idCard[1].split("");
        const sum = digits.reduce((total: number, digit: string, i: number) => {
          return total + (13 - i) * +digit;
        }, 0);
        const lastDigit = `${(11 - (sum % 11)) % 10}`;
        const inputLastDigit = idCard[2];
        if (lastDigit !== inputLastDigit) {
          return Promise.reject(
            genErrorMessage(
              t("form:validation.idCardValidation"),
              ErrorMessageComponent
            )
          );
        }
      }

      if (isFullName) {
        const nameRegex = /^[A-Za-zก-๙]+(?: [A-Za-zก-๙]+)*$/;
        if (value && !nameRegex.test(value)) {
          return Promise.reject(
            genErrorMessage(
              t("form:validation.fullNameValidation"),
              ErrorMessageComponent
            )
          );
        }
      }

      if (emailValidation) {
        if (value && !EMAIL_PATTERN.test(value)) {
          return Promise.reject(
            genErrorMessage(
              t("form:validation.emailValidation", { ns: "driver-info" }),
              ErrorMessageComponent
            )
          );
        }
      }

      if (phoneNumberValidation) {
        if (value && !E164_CONTACT_NO_PATTERN.test(value)) {
          return Promise.reject(
            genErrorMessage(
              t("form:validation.mobileNoValidation", { ns: "driver-info" }),
              ErrorMessageComponent
            )
          );
        }
      }

      return Promise.resolve();
    },
  },
];

export const validateBmi = (
  validations: FieldValidationConfigType[],
  t: Function,
  sectionCode: string,
  inputCode: string,
  form: FormInstance,
  ErrorMessageComponent: React.ElementType = null
) => [
  {
    validator: async (_: any, value: number = 0) => {
      const bmiValidation = validations?.find(
        (validation) => validation.rule === RULE_CODE.BMI
      );
      if (bmiValidation) {
        const min = bmiValidation?.params?.min || 0;
        const max = bmiValidation?.params?.max || 0;

        const weightInputCode = bmiValidation?.params?.mapInputCode?.find(
          (code: { param: string }) => code.param === "weight"
        )?.param;
        const heightInputCode = bmiValidation?.params?.mapInputCode?.find(
          (code: { param: string }) => code.param === "height"
        )?.param;
        const weightInputValue =
          form.getFieldValue([sectionCode, weightInputCode]) || "";
        const heightInputValue =
          form.getFieldValue([sectionCode, heightInputCode]) || "";

        const isWeightValid = weightInputValue.match(DECIMAL_PATTERN);
        const isHeightValid = heightInputValue.match(DECIMAL_PATTERN);

        if (isWeightValid && isHeightValid) {
          const weight = weightInputValue || 0;
          const height = heightInputValue || 0;

          const calculatedBMI = weight / (height / 100) ** 2;
          const errorMessage = genErrorMessage(
            t("form:validation.invalidBMI"),
            ErrorMessageComponent
          );
          const fieldName =
            inputCode !== weightInputCode ? weightInputCode : heightInputCode;

          // back up old errors and remove bmi validate errors
          const fieldErrors = form
            .getFieldError([sectionCode, fieldName])
            .filter((error: string | JSX.Element) => {
              if (
                typeof error === "object" &&
                typeof errorMessage === "object"
              ) {
                return error.props.children !== errorMessage.props.children;
              }
              return error !== errorMessage;
            });
          if (weight <= 0) {
            if (height) {
              form.setFields([
                {
                  name: [sectionCode, heightInputCode],
                  errors: [],
                },
              ]);
            }

            return Promise.resolve();
          }

          if (height <= 0) {
            if (weight) {
              form.setFields([
                {
                  name: [sectionCode, weightInputCode],
                  errors: [],
                },
              ]);
            }
            return Promise.resolve();
          }

          if (
            isFinite(calculatedBMI) &&
            ((calculatedBMI > max && max) ||
              (calculatedBMI < min && min) ||
              calculatedBMI <= 0)
          ) {
            // update pair params bmi errors
            form.setFields([
              {
                name: [sectionCode, fieldName],
                errors: [errorMessage as string],
              },
            ]);

            return Promise.reject(errorMessage);
          }

          if (
            weight <= 0 ||
            height <= 0 ||
            (isFinite(calculatedBMI) &&
              ((calculatedBMI > max && max) ||
                (calculatedBMI < min && min) ||
                calculatedBMI <= 0))
          ) {
            // update pair params bmi errors
            form.setFields([
              {
                name: [sectionCode, fieldName],
                errors: [errorMessage as string],
              },
            ]);

            return Promise.reject(errorMessage);
          }

          // remove bmi validate errors
          form.setFields([
            {
              name: [sectionCode, fieldName],
              errors: [...fieldErrors],
            },
          ]);
        }
      }

      return Promise.resolve();
    },
  },
];

/**
 * Check format of license plate
 * the format is ก-ฮ or a-z or A-D at least 1 character and end with 3 arabic numeral
 * example:
 * 1AB4444 is pass,
 * 9 is fail,
 * กV3333 is pass,
 * DV333A is fail,
 * @param value String that you want to check
 * @returns If match, it will return true
 */
export const validateLicensePlate = (value: string): boolean => {
  const result = /^(?=.*[0-9])(?=.*[\u0E00-\u0E7Fa-zA-Z])(.*[0-9])$/.test(
    value
  );
  return result;
};

export const validateAge = (
  validations: FieldValidationConfigType[],
  t: Function,
  refDate: string,
  ErrorMessageComponent: React.ElementType = null,
  errorMessage?: string
) => [
  {
    validator: async (_: any, value: Date) => {
      if (value) {
        const dob = formatDateText(value);
        const ageByYear0Validation = validations?.find(
          (validation) => validation.rule === RULE_CODE.AGE_BY_YEAR_0
        );

        const ageByDay0Validation = validations?.find(
          (validation) => validation.rule === RULE_CODE.AGE_BY_DAY_0
        );

        if (ageByYear0Validation) {
          const ageByYear0 = calcAgeByYear0(refDate, dob);
          const min = ageByYear0Validation?.params?.min || 0;
          const max = ageByYear0Validation?.params?.max || Infinity;
          if (ageByYear0 < min || ageByYear0 > max) {
            const validErrorMessage =
              errorMessage ||
              genErrorMessage(
                t("form:validation.invalidAge"),
                ErrorMessageComponent
              );
            return Promise.reject(validErrorMessage);
          }
        }

        if (ageByDay0Validation) {
          const age = calcAgeByDay0(refDate, dob);
          const min = ageByDay0Validation?.params?.min || 0;
          const max = ageByDay0Validation?.params?.max || Infinity;
          if (age < min || age > max) {
            const validErrorMessage =
              errorMessage ||
              genErrorMessage(
                t("form:validation.invalidAge"),
                ErrorMessageComponent
              );
            return Promise.reject(validErrorMessage);
          }
        }
      }

      return Promise.resolve();
    },
  },
];
